import { useEffect, useState } from 'react';
import type { FocusEvent } from 'react';
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Tooltip,
  Typography
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

import { FeatureDialog } from './components/feature-dialog';
import { useStyles } from './styles';
import type { BannerNotification } from './types';

interface FeatureBannerProps {
  notifications?: BannerNotification[];
  onDismiss?: (id: string) => void;
  /** Time each message stays visible before auto-advancing (ms). */
  rotateInterval?: number;
}

const DEFAULT_ROTATE_INTERVAL = 12000;
const TRANSITION_DURATION = 300;

// Tracks the OS-level "reduce motion" accessibility preference.
const usePrefersReducedMotion = () => {
  const query = '(prefers-reduced-motion: reduce)';
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches
  );

  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (event: MediaQueryListEvent) => setReduced(event.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  return reduced;
};

export const FeatureBanner = ({
  notifications = [],
  onDismiss,
  rotateInterval = DEFAULT_ROTATE_INTERVAL
}: FeatureBannerProps) => {
  const classes = useStyles();
  const reducedMotion = usePrefersReducedMotion();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [keyboardFocus, setKeyboardFocus] = useState(false);
  // Drives the collapse-out animation when the banner is being dismissed.
  const [closing, setClosing] = useState(false);

  // Pause auto-advance while the pointer is over the banner or a keyboard user
  // is focused inside it.
  const paused = hovered || keyboardFocus;

  const count = notifications.length;
  const hasCarousel = count > 1;

  // Keep the active index in range as notifications get dismissed/refetched.
  useEffect(() => {
    if (count > 0 && activeIndex > count - 1) setActiveIndex(count - 1);
  }, [count, activeIndex]);

  if (count === 0) return null;

  const safeIndex = Math.min(activeIndex, count - 1);
  const active = notifications[safeIndex];

  // X dismisses only the notification currently in view (dismissal is persisted
  // server-side, so we must never dismiss carousel banners the user hasn't seen).
  // On the last remaining one, animate the whole banner away first; otherwise
  // drop it immediately and let the carousel fall through to the next.
  const handleDismiss = () => {
    if (count <= 1) {
      setClosing(true);
      return;
    }
    onDismiss?.(active.id);
  };

  // Resetting `closing` afterwards lets a future notification show again.
  const handleExited = () => {
    setClosing(false);
    onDismiss?.(active.id);
  };

  const goTo = (index: number) => setActiveIndex((index + count) % count);

  // Only treat *keyboard* focus as a pause trigger. A mouse click also focuses
  // the "Learn more" button — and the dialog restores that focus when it closes
  // — which must not keep the timer paused once the pointer has left the banner.
  const isFocusVisible = (target: EventTarget | null) => {
    try {
      return target instanceof Element && target.matches(':focus-visible');
    } catch {
      return false;
    }
  };

  const handleFocus = (event: FocusEvent<HTMLDivElement>) => {
    if (isFocusVisible(event.target)) setKeyboardFocus(true);
  };

  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node))
      setKeyboardFocus(false);
  };

  return (
    <>
      <Collapse
        in={!closing}
        appear
        timeout={reducedMotion ? 0 : TRANSITION_DURATION}
        onExited={handleExited}>
        <Box
          className={classes.banner}
          role="status"
          aria-live="polite"
          aria-atomic="true"
          aria-label="Notifications"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onFocus={handleFocus}
          onBlur={handleBlur}>
          {hasCarousel && (
            <IconButton
              size="small"
              className={classes.navButton}
              onClick={() => goTo(safeIndex - 1)}
              aria-label="Previous notification">
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          )}

          <Box className={classes.content}>
            <Tooltip title={active.message} arrow>
              <Typography className={classes.message}>
                {active.message}
              </Typography>
            </Tooltip>
            {(active.actionLabel ||
              active.dialogTitle ||
              active.dialogBody?.length) && (
              <Button
                className={classes.actionButton}
                disableElevation
                onClick={() => setDialogOpen(true)}>
                {active.actionLabel ?? 'Learn more'}
              </Button>
            )}
          </Box>

          {hasCarousel && (
            <IconButton
              size="small"
              className={classes.navButton}
              onClick={() => goTo(safeIndex + 1)}
              aria-label="Next notification">
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          )}

          <IconButton
            size="small"
            className={classes.closeButton}
            onClick={handleDismiss}
            aria-label="Dismiss banner">
            <CloseIcon fontSize="small" />
          </IconButton>

          {hasCarousel && !reducedMotion && (
            <Box className={classes.progressTrack}>
              <Box
                key={safeIndex}
                className={classes.progressBar}
                style={{
                  animationDuration: `${rotateInterval}ms`,
                  animationPlayState:
                    paused || dialogOpen ? 'paused' : 'running'
                }}
                onAnimationEnd={() =>
                  setActiveIndex(prev => (prev + 1) % count)
                }
              />
            </Box>
          )}
        </Box>
      </Collapse>
      <FeatureDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={active.dialogTitle}
        body={active.dialogBody}
      />
    </>
  );
};
